/**
 * AdsTab — 광고 ROAS 탭 (유입·광고 분석 셸의 탭 본문)
 *
 * 요약 KPI + 채널별 ROAS 카드 + 일자별 추이 콤보차트 + 일자별 상세 테이블
 * + 인사이트(규칙기반 + AI) + 광고비 입력 CRUD.
 * 기간(from/to)은 셸에서 date-only(YYYY-MM-DD)로 주입받는다(자체 기간 필터 없음).
 * 데이터 소스: GET /api/admin/dashboard/ads?from&to.
 *   총 매출·전체 ROAS·일별 순매출 = 자사 실결제 순매출(payments, KST). 이게 헤드라인 지표다.
 *   채널별 전환매출(conversionValue)은 광고 플랫폼(메타/네이버) 리포트값으로 실결제와 다른 참고 지표다.
 *   채널별 실결제 ROAS는 '유입·광고 분석 > 채널 퍼널' 참고.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useApiFetch } from '@/hooks/useApiFetch'
import { apiFetch } from '@/lib/api'
import { useToast } from '@/components/ToastContainer'
import {
  Card,
  DatePicker,
  Select,
  Input,
  Button,
  Modal,
  ConfirmDialog,
  Spinner,
  Alert,
  FileUpload,
  StatCard,
  Table,
  SpendRevenueRoasChart,
  formatNumber,
  formatCurrency,
  type TableColumn,
  type SpendRevenueRoasDatum,
} from '@/ui'
import { parseAdSpendXlsx, type AdSpendDraft, type AdChannel } from '@/lib/adSpendParser'
import { buildRuleInsights, type AdsReportForInsights } from '@/lib/adInsights'
import styles from './AdsTab.module.css'

interface MetricPair {
  current: number
  previous: number
}
interface NullablePair {
  current: number | null
  previous: number | null
}
interface DailyChannel {
  spend: number
  revenue: number
}
interface AdsReport {
  from: string
  to: string
  summary: {
    spend: MetricPair
    revenue: MetricPair
    impressions: MetricPair
    clicks: MetricPair
    roas: NullablePair
    cac: NullablePair
  }
  daily: {
    date: string
    spend: number
    revenue: number
    roas: number | null
    byChannel: { META: DailyChannel; NAVER: DailyChannel }
  }[]
  channels: { channel: string; spend: number; conversionValue: number; roas: number | null; impressions: number; clicks: number }[]
  table: { date: string; channel: string; spend: number; conversionValue: number; impressions: number; clicks: number }[]
}

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/

// 채널 표시 라벨 (짧은 형태 — 카드/테이블용)
const CHANNEL_LABEL: Record<string, string> = { NAVER: '네이버', META: '인스타그램' }

// ── 수동 입력 ──
const CHANNEL_OPTIONS: { value: AdChannel; label: string }[] = [
  { value: 'META', label: '인스타/페북 (META)' },
  { value: 'NAVER', label: '네이버 검색광고 (NAVER)' },
]
const CHANNEL_LABEL_FULL: Record<string, string> = { NAVER: '네이버 검색광고', META: '인스타/페북' }

/** 이미 저장된 광고비 행 (GET /ads/spend). */
interface AdSpendRow {
  id: string
  channel: string
  spendDate: string
  spend: number
  conversionValue: number
  impressions: number
  clicks: number
  source: string
}

/** 편집 중인 수동 입력 행 — 파서 출력(AdSpendDraft)에 UI 식별자 key 추가. */
interface DraftRow extends AdSpendDraft {
  key: string
}

const SOURCE_LABEL: Record<string, string> = { manual: '수동', meta: 'Meta API', naver: '네이버 API' }

let draftSeq = 0
function newDraft(): DraftRow {
  return { key: `d${draftSeq++}`, channel: 'META', spendDate: toYmd(new Date()), spend: '', conversionValue: '', impressions: '', clicks: '' }
}

/** 파서 결과 행 → 편집 draft 행(key 부여). */
function toDraftRow(input: AdSpendDraft): DraftRow {
  return { key: `d${draftSeq++}`, ...input }
}

/** Date → YYYY-MM-DD (로컬=KST 기준). */
function toYmd(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** ₩ 접두 통화 표기. */
function won(n: number): string {
  return `₩${formatCurrency(n)}`
}

/** ROAS(%) — 원 값에서 직접 계산해 소수 1자리로 표기(사전 반올림된 roas 필드보다 정확). */
function roasPct(revenue: number, spend: number): string {
  return spend > 0 ? `${((revenue / spend) * 100).toFixed(1)}%` : '-'
}

/** 비중(%) 표기. */
function sharePct(part: number, total: number): string {
  return total > 0 ? `${((part / total) * 100).toFixed(1)}%` : '-'
}

/** 채널 배열 → 채널키 조회 맵. */
function indexChannels(channels: AdsReport['channels']): Record<string, AdsReport['channels'][number]> {
  const map: Record<string, AdsReport['channels'][number]> = {}
  for (const ch of channels) map[ch.channel] = ch
  return map
}

/** 채널별 ROAS 카드 — 전환매출/지출 → ROAS%. */
function ChannelRoasCard({ label, conversionValue, spend }: { label: string; conversionValue: number; spend: number }) {
  const positive = spend > 0 && conversionValue >= spend
  return (
    <div className={styles.roasCard}>
      <p className={styles.roasCardLabel}>
        {label} (전환매출 {won(conversionValue)} / 지출 {won(spend)})
      </p>
      <p className={`${styles.roasCardValue} ${positive ? styles.roasPositive : styles.roasNegative}`}>
        {roasPct(conversionValue, spend)}
      </p>
    </div>
  )
}

/** 셸(부모) 필터바에 노출할 광고비 액션 — 엑셀 업로드/광고비 입력 모달 열기. */
export interface AdsTabActions {
  openUpload: () => void
  openInput: () => void
}

interface AdsTabProps {
  /** 셸에서 주입되는 조회 기간 — date-only(YYYY-MM-DD). ads API 스키마와 동일. */
  from: string
  to: string
  /**
   * 엑셀 업로드/광고비 입력 버튼을 셸 필터바에서 렌더하도록 액션을 등록한다.
   * 모달·로직은 이 탭이 소유하고, 버튼(트리거)만 부모가 필터바 한 줄에 그린다.
   * 언마운트 시 null 로 등록 해제.
   */
  onRegisterActions?: (actions: AdsTabActions | null) => void
}

export function AdsTab({ from, to, onRegisterActions }: AdsTabProps) {
  const { addToast } = useToast()

  const { data, error, refetch } = useApiFetch<AdsReport>(`/api/admin/dashboard/ads?from=${from}&to=${to}`)
  // 기간 변경 갱신 표시: 요청 from/to 와 응답 data.from/to 가 다르면 갱신 중 (SWR 패턴 대응).
  const refreshing = !error && (!data || data.from !== from || data.to !== to)

  // 기간 내 이미 저장된 광고비 행 (수동 입력 내역 테이블 + 삭제용)
  const { data: spendRows, refetch: refetchSpend } = useApiFetch<AdSpendRow[]>(
    `/api/admin/dashboard/ads/spend?from=${from}&to=${to}`,
  )

  // 수동 입력 편집 행 목록 (초기 1행)
  const [drafts, setDrafts] = useState<DraftRow[]>(() => [newDraft()])
  const [saving, setSaving] = useState(false)
  const [inputModalOpen, setInputModalOpen] = useState(false)
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)

  // 엑셀 파싱 경고(건너뛴 행) — 편집 모달 상단 배너에 표시
  const [warnings, setWarnings] = useState<string[]>([])
  // 업로드 다이얼로그
  const [uploadOpen, setUploadOpen] = useState(false)
  const [uploadChannel, setUploadChannel] = useState<AdChannel>('META')
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [uploadParsing, setUploadParsing] = useState(false)

  // AI 인사이트
  const [aiInsights, setAiInsights] = useState<string[] | null>(null)
  const [aiLoading, setAiLoading] = useState(false)
  const [aiModel, setAiModel] = useState<'haiku' | 'sonnet' | 'opus'>('haiku')

  // 모달 열 때 입력 행 초기화. initialRows 있으면 그걸로(엑셀 업로드), 없으면 빈 1행(수동).
  const openInputModal = (initialRows?: AdSpendDraft[]) => {
    setDrafts(initialRows && initialRows.length ? initialRows.map(toDraftRow) : [newDraft()])
    setInputModalOpen(true)
  }

  const openUploadModal = useCallback(() => {
    setUploadChannel('META')
    setUploadError(null)
    setUploadOpen(true)
  }, [])

  // 광고비 입력 버튼 동작 — 경고 초기화 후 빈 입력 모달 열기.
  const openInputFromToolbar = useCallback(() => {
    setWarnings([])
    setDrafts([newDraft()])
    setInputModalOpen(true)
  }, [])

  // 엑셀 업로드/광고비 입력 버튼을 셸 필터바로 노출(등록). 언마운트 시 해제.
  useEffect(() => {
    onRegisterActions?.({ openUpload: openUploadModal, openInput: openInputFromToolbar })
    return () => onRegisterActions?.(null)
  }, [onRegisterActions, openUploadModal, openInputFromToolbar])

  // FileUpload onChange — 클릭/드래그로 선택된 파일을 파싱. (단일 파일)
  const handleUploadFile = async (files: File[]) => {
    const file = files[0]
    if (!file) return
    setUploadParsing(true)
    setUploadError(null)
    try {
      const { rows, warnings: warns } = await parseAdSpendXlsx(file, uploadChannel)
      setUploadOpen(false)
      setWarnings(warns)
      openInputModal(rows)
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : '엑셀 처리 실패')
    } finally {
      setUploadParsing(false)
    }
  }

  const updateDraft = (key: string, patch: Partial<DraftRow>) =>
    setDrafts((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)))
  const addDraft = () => setDrafts((rows) => [...rows, newDraft()])
  const removeDraft = (key: string) =>
    setDrafts((rows) => (rows.length > 1 ? rows.filter((r) => r.key !== key) : rows))

  // 유효 행만 서버 전송용 item 으로 변환. 광고비 미입력/음수·날짜 형식 오류 행은 제외.
  const handleSaveAll = useCallback(async () => {
    if (saving) return
    const items: { channel: AdChannel; spendDate: string; spend: number; conversionValue: number; impressions: number; clicks: number }[] = []
    const seen = new Set<string>()
    for (const r of drafts) {
      if (!YMD_RE.test(r.spendDate)) continue
      const spend = Number(r.spend)
      if (r.spend.trim() === '' || !Number.isFinite(spend) || spend < 0) continue
      const key = `${r.channel}:${r.spendDate}`
      if (seen.has(key)) {
        addToast({ type: 'error', message: `중복된 입력: ${CHANNEL_LABEL_FULL[r.channel]} ${r.spendDate}` })
        return
      }
      seen.add(key)
      const conversionValue = Number(r.conversionValue) || 0
      const impressions = Number(r.impressions) || 0
      const clicks = Number(r.clicks) || 0
      items.push({
        channel: r.channel,
        spendDate: r.spendDate,
        spend: Math.round(spend),
        conversionValue: conversionValue >= 0 ? Math.round(conversionValue) : 0,
        impressions: impressions >= 0 ? Math.round(impressions) : 0,
        clicks: clicks >= 0 ? Math.round(clicks) : 0,
      })
    }
    if (items.length === 0) {
      addToast({ type: 'error', message: '저장할 광고비를 입력하세요 (날짜·광고비 필수)' })
      return
    }
    setSaving(true)
    try {
      // admin의 apiFetch는 성공 시 data를 언랩해 반환하고 실패 시 throw 한다
      await apiFetch('/api/admin/dashboard/ads/spend', {
        method: 'POST',
        body: JSON.stringify({ items }),
      })
      addToast({ type: 'success', message: `광고비 ${items.length}건 저장됨` })
      setDrafts([newDraft()])
      setInputModalOpen(false)
      setWarnings([])
      refetch()
      refetchSpend()
    } catch (e) {
      addToast({ type: 'error', message: e instanceof Error ? e.message : '저장 실패' })
    } finally {
      setSaving(false)
    }
  }, [saving, drafts, addToast, refetch, refetchSpend])

  const handleDeleteSpend = useCallback(
    async (id: string) => {
      try {
        await apiFetch(`/api/admin/dashboard/ads/spend/${id}`, { method: 'DELETE' })
        addToast({ type: 'success', message: '삭제됨' })
        refetch()
        refetchSpend()
      } catch (e) {
        addToast({ type: 'error', message: e instanceof Error ? e.message : '삭제 실패' })
      } finally {
        setPendingDeleteId(null)
      }
    },
    [addToast, refetch, refetchSpend],
  )

  // ── 파생 데이터 ──
  const s = data?.summary
  const channelMap = useMemo(() => (data ? indexChannels(data.channels) : {}), [data])
  const metaSpend = channelMap.META?.spend ?? 0
  const naverSpend = channelMap.NAVER?.spend ?? 0
  // 인스타 전환매출(메타 광고 리포트값) — 참고 지표. 네이버 전환매출은 스마트스토어
  // 구조상 측정 불가(항상 0)라 표시하지 않는다.
  const metaRev = channelMap.META?.conversionValue ?? 0

  // 채널별 ROAS 카드 — 인스타(META)만. 네이버는 전환매출을 측정할 수 없어 제외.
  const roasCards = useMemo(() => {
    if (!data) return []
    return data.channels.filter((ch) => ch.channel === 'META' && (ch.spend > 0 || ch.conversionValue > 0))
  }, [data])

  // 콤보 차트 데이터 — 광고비 스택(네이버+인스타) + 인스타 전환매출(참고) + 실결제 ROAS 라인.
  // ROAS는 상단 KPI·상세표와 동일하게 자사 실결제 순매출/광고비 기준으로 통일한다.
  const chartData: SpendRevenueRoasDatum[] = useMemo(() => {
    if (!data) return []
    return data.daily.map((d) => ({
      date: d.date,
      spendMeta: d.byChannel.META.spend,
      spendNaver: d.byChannel.NAVER.spend,
      revMeta: d.byChannel.META.revenue,
      roas: d.spend > 0 ? d.revenue / d.spend : null,
    }))
  }, [data])

  // 일자별 상세 테이블 (일자별 1행 + 합계행)
  const detailRows: DetailRow[] = useMemo(() => {
    if (!data) return []
    // revenue/roasText = 실결제 순매출 기준(총). 인스타 전환매출은 메타 광고 리포트값(참고).
    const rows: DetailRow[] = data.daily.map((d) => ({
      key: d.date,
      date: d.date,
      revenue: d.revenue,
      spend: d.spend,
      roasText: roasPct(d.revenue, d.spend),
      metaRev: d.byChannel.META.revenue,
      spendBreakdown: `인스타(${formatCurrency(d.byChannel.META.spend)}) & 네이버(${formatCurrency(d.byChannel.NAVER.spend)})`,
      isTotal: false,
    }))
    if (rows.length > 0) {
      const totalRev = data.summary.revenue.current
      const totalSpend = data.summary.spend.current
      rows.push({
        key: '__total__',
        date: '합계',
        revenue: totalRev,
        spend: totalSpend,
        roasText: roasPct(totalRev, totalSpend),
        metaRev: channelMap.META?.conversionValue ?? 0,
        spendBreakdown: `인스타(${formatCurrency(metaSpend)}) & 네이버(${formatCurrency(naverSpend)})`,
        isTotal: true,
      })
    }
    return rows
  }, [data, channelMap, metaSpend, naverSpend])

  // 규칙기반 인사이트 (즉시)
  const ruleInsights = useMemo(() => (data ? buildRuleInsights(data as AdsReportForInsights) : []), [data])

  // AI 인사이트 생성
  const handleGenerateInsights = useCallback(async () => {
    if (!data || aiLoading) return
    setAiLoading(true)
    try {
      const res = await apiFetch<{ insights: string[] }>('/api/admin/dashboard/ads/insights', {
        method: 'POST',
        body: JSON.stringify({
          from: data.from,
          to: data.to,
          model: aiModel,
          summary: {
            spend: data.summary.spend.current,
            revenue: data.summary.revenue.current,
            roas: data.summary.spend.current > 0 ? data.summary.revenue.current / data.summary.spend.current : null,
          },
          channels: data.channels.map((ch) => ({
            channel: ch.channel,
            spend: ch.spend,
            conversionValue: ch.conversionValue,
            roas: ch.spend > 0 ? ch.conversionValue / ch.spend : null,
          })),
          daily: data.daily.map((d) => ({
            date: d.date,
            spend: d.spend,
            revenue: d.revenue,
            roas: d.spend > 0 ? d.revenue / d.spend : null,
          })),
        }),
      })
      setAiInsights(res.insights)
    } catch (e) {
      addToast({ type: 'error', message: e instanceof Error ? e.message : 'AI 인사이트 생성 실패' })
    } finally {
      setAiLoading(false)
    }
  }, [data, aiLoading, aiModel, addToast])

  const spendColumns: TableColumn<AdSpendRow>[] = [
    { key: 'spendDate', label: '날짜' },
    { key: 'channel', label: '채널', render: (r) => CHANNEL_LABEL_FULL[r.channel] ?? r.channel },
    { key: 'spend', label: '광고비', align: 'right', render: (r) => won(r.spend) },
    { key: 'conversionValue', label: '전환매출', align: 'right', render: (r) => won(r.conversionValue) },
    { key: 'impressions', label: '노출', align: 'right', render: (r) => formatNumber(r.impressions) },
    { key: 'clicks', label: '클릭', align: 'right', render: (r) => formatNumber(r.clicks) },
    { key: 'source', label: '출처', render: (r) => SOURCE_LABEL[r.source] ?? r.source },
    {
      key: 'action',
      label: '',
      align: 'right',
      render: (r) => (
        <Button variant="ghost" size="sm" onClick={() => setPendingDeleteId(r.id)}>
          삭제
        </Button>
      ),
    },
  ]

  return (
    <div className={styles.root}>
      {/* 갱신 스피너 — 엑셀 업로드/광고비 입력 버튼은 셸 필터바로 노출(onRegisterActions). */}
      {refreshing && (
        <div className={styles.refreshRow}>
          <Spinner size="sm" />
        </div>
      )}

      {error && <Alert variant="danger">불러오지 못했습니다: {error}</Alert>}

      {/* 요약 KPI */}
      <section>
        <p className={styles.sectionTitle}>요약</p>
        <div className={styles.kpiGrid}>
          <StatCard label="총 매출(실결제)" value={s ? won(s.revenue.current) : '—'} />
          <StatCard label="총 광고비" value={s ? won(s.spend.current) : '—'} />
          <StatCard label="ROAS(실결제)" value={s ? roasPct(s.revenue.current, s.spend.current) : '—'} />
          <StatCard
            label="채널 비중(지출)"
            value={s ? `인스타 ${sharePct(metaSpend, metaSpend + naverSpend)}` : '—'}
            sub={s ? `네이버 ${sharePct(naverSpend, metaSpend + naverSpend)}` : undefined}
          />
          <StatCard label="인스타 전환매출(광고)" value={s ? won(metaRev) : '—'} />
        </div>
        <Alert variant="info" className={styles.revenueNote}>
          총 매출·ROAS는 자사 실결제 순매출(결제액−환불액, 결제완료·부분환불) 기준입니다.
          인스타 전환매출은 메타 광고 리포트값으로 실결제와 다른 참고 지표입니다.
          네이버(스마트스토어)는 결제가 네이버에서 완결돼 전환매출을 측정할 수 없어 표시하지 않습니다.
        </Alert>
      </section>

      {/* 채널별 ROAS */}
      <section>
        <p className={styles.sectionTitle}>채널별 ROAS</p>
        {!data ? (
          <div className={styles.center}><Spinner /></div>
        ) : roasCards.length > 0 ? (
          <div className={styles.roasGrid}>
            {roasCards.map((ch) => (
              <ChannelRoasCard
                key={ch.channel}
                label={CHANNEL_LABEL[ch.channel] ?? ch.channel}
                conversionValue={ch.conversionValue}
                spend={ch.spend}
              />
            ))}
          </div>
        ) : (
          <Alert variant="info">해당 기간 광고비 데이터가 없습니다.</Alert>
        )}
      </section>

      {/* 일자별 추이 */}
      <section>
        <p className={styles.sectionTitle}>일자별 추이</p>
        <Card>
          <div className={styles.chartBody}>
            {!data ? (
              <div className={styles.center}><Spinner /></div>
            ) : chartData.length > 0 ? (
              <SpendRevenueRoasChart data={chartData} />
            ) : (
              <Alert variant="info">해당 기간 데이터가 없습니다.</Alert>
            )}
          </div>
        </Card>
      </section>

      {/* 일자별 상세 */}
      <section>
        <p className={styles.sectionTitle}>일자별 상세</p>
        <Card>
          <Table
            size="sm"
            columns={DETAIL_COLUMNS}
            data={detailRows}
            rowKey={(r) => r.key}
            emptyMessage="해당 기간 광고비 데이터가 없습니다."
          />
        </Card>
      </section>

      {/* 인사이트 */}
      <section>
        <div className={styles.insightHead}>
          <p className={styles.sectionTitle}>인사이트</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--ds-space-2)' }}>
            <Select
              size="sm"
              aria-label="AI 인사이트 모델"
              value={aiModel}
              onChange={(e) => setAiModel(e.target.value as 'haiku' | 'sonnet' | 'opus')}
              disabled={aiLoading}
              options={[
                { value: 'haiku', label: 'Haiku (빠름)' },
                { value: 'sonnet', label: 'Sonnet (중간)' },
                { value: 'opus', label: 'Opus (고급)' },
              ]}
            />
            <Button variant="secondary" size="sm" onClick={handleGenerateInsights} loading={aiLoading} disabled={!data}>
              AI 인사이트 생성
            </Button>
          </div>
        </div>
        <div className={styles.insightList}>
          {ruleInsights.map((text, i) => (
            <div key={`rule-${i}`} className={styles.insightCard}>
              {text}
            </div>
          ))}
          {aiInsights?.map((text, i) => (
            <div key={`ai-${i}`} className={`${styles.insightCard} ${styles.insightAi}`}>
              <span className={styles.aiTag}>AI</span> {text}
            </div>
          ))}
          {ruleInsights.length === 0 && !aiInsights && (
            <Alert variant="info">인사이트를 표시할 데이터가 부족합니다.</Alert>
          )}
        </div>
      </section>

      {/* 광고비 입력 내역 (기간 내 저장된 광고비 — 삭제 가능) */}
      <section>
        <p className={styles.sectionTitle}>광고비 입력 내역</p>
        <Card>
          <Table
            size="sm"
            striped
            columns={spendColumns}
            data={spendRows ?? []}
            rowKey={(r) => r.id}
            emptyMessage="해당 기간 입력된 광고비가 없습니다."
          />
        </Card>
      </section>

      {/* 광고비 수동 입력 모달 (Meta/Naver API 미연동 시) */}
      <Modal
        open={inputModalOpen}
        onClose={() => {
          setInputModalOpen(false)
          setWarnings([])
        }}
        title="광고비 수동 입력"
        size="lg"
      >
        <p className={styles.hint}>
          일자별 광고비·전환매출을 직접 입력하면 ROAS 리포트에 바로 반영됩니다.
          같은 날짜·채널을 다시 저장하면 기존 값을 덮어씁니다.
          <br />
          META는 광고 관리자 내보내기의 지출 금액(Spend)·구매 전환값(Purchases conversion value)·노출(Impressions)·클릭(전체)(Clicks, all) 값을 그대로 입력하세요.
        </p>
        {warnings.length > 0 && (
          <Alert variant="warning" className={styles.warnAlert}>
            {warnings.length}개 행을 건너뛰었습니다.
            <ul className={styles.warnList}>
              {warnings.slice(0, 10).map((w, i) => (
                <li key={i}>{w}</li>
              ))}
              {warnings.length > 10 && <li>… 외 {warnings.length - 10}건</li>}
            </ul>
          </Alert>
        )}
        <div className={styles.draftList}>
          {drafts.map((r) => (
            <div key={r.key} className={styles.draftRow}>
              {/* 윗줄: 날짜 + 채널 + 제거 */}
              <div className={styles.draftTop}>
                <DatePicker
                  size="sm"
                  popoverPortal
                  className={styles.draftDate}
                  value={r.spendDate}
                  onChange={(_d, formatted) => updateDraft(r.key, { spendDate: formatted })}
                />
                <Select
                  size="sm"
                  className={styles.draftChannel}
                  value={r.channel}
                  options={CHANNEL_OPTIONS}
                  onChange={(e) => updateDraft(r.key, { channel: e.target.value as AdChannel })}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className={styles.draftRemove}
                  onClick={() => removeDraft(r.key)}
                  disabled={drafts.length <= 1}
                >
                  제거
                </Button>
              </div>
              {/* 아랫줄: 광고비 + 전환매출 + 노출 + 클릭 */}
              <div className={styles.draftBottom}>
                <Input
                  size="sm"
                  type="number"
                  min={0}
                  className={styles.draftNum}
                  placeholder="광고비 / Spend (KRW)"
                  value={r.spend}
                  onChange={(e) => updateDraft(r.key, { spend: e.target.value })}
                />
                <Input
                  size="sm"
                  type="number"
                  min={0}
                  className={styles.draftNum}
                  placeholder="전환매출 / Conversion value (KRW)"
                  value={r.conversionValue}
                  onChange={(e) => updateDraft(r.key, { conversionValue: e.target.value })}
                />
                <Input
                  size="sm"
                  type="number"
                  min={0}
                  className={styles.draftNum}
                  placeholder="노출 / Impressions"
                  value={r.impressions}
                  onChange={(e) => updateDraft(r.key, { impressions: e.target.value })}
                />
                <Input
                  size="sm"
                  type="number"
                  min={0}
                  className={styles.draftNum}
                  placeholder="클릭(전체) / Clicks (all)"
                  value={r.clicks}
                  onChange={(e) => updateDraft(r.key, { clicks: e.target.value })}
                />
              </div>
            </div>
          ))}
        </div>
        <div className={styles.draftActions}>
          <Button variant="secondary" size="sm" onClick={addDraft}>
            + 행 추가
          </Button>
          <Button variant="primary" size="sm" onClick={handleSaveAll} loading={saving}>
            모두 저장
          </Button>
        </div>
      </Modal>

      {/* 광고비 엑셀 업로드 다이얼로그 */}
      <Modal open={uploadOpen} onClose={() => setUploadOpen(false)} title="광고비 엑셀 업로드" size="sm">
        <p className={styles.hint}>
          메타 광고 관리자에서 기간을 정하고 '일별 분석'으로 내보낸 엑셀(.xlsx)을 올리세요.
          지출 금액과 함께 '구매 전환값' 컬럼이 있으면 전환매출로 함께 읽습니다.
          같은 날짜의 여러 캠페인 행은 자동 합산됩니다.
        </p>
        <Select
          size="sm"
          label="채널"
          value={uploadChannel}
          options={CHANNEL_OPTIONS}
          onChange={(e) => setUploadChannel(e.target.value as AdChannel)}
        />
        <FileUpload
          className={styles.uploadField}
          label="엑셀 파일"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          placeholder="엑셀 파일을 여기로 드래그하거나 클릭해서 선택하세요 (.xlsx)"
          loading={uploadParsing}
          onChange={handleUploadFile}
        />
        {uploadError && <Alert variant="danger">{uploadError}</Alert>}
      </Modal>

      <ConfirmDialog
        open={pendingDeleteId != null}
        onClose={() => setPendingDeleteId(null)}
        onConfirm={() => pendingDeleteId && handleDeleteSpend(pendingDeleteId)}
        title="광고비 삭제"
        message="이 광고비 입력을 삭제하시겠습니까? ROAS 리포트에서 즉시 제외됩니다."
        confirmLabel="삭제"
        variant="danger"
      />
    </div>
  )
}

/** 일자별 상세 테이블 행. */
interface DetailRow {
  key: string
  date: string
  revenue: number
  spend: number
  roasText: string
  metaRev: number
  spendBreakdown: string
  isTotal: boolean
}

const boldIfTotal = (r: DetailRow, node: ReactNode) => (r.isTotal ? <strong>{node}</strong> : node)

const DETAIL_COLUMNS: TableColumn<DetailRow>[] = [
  { key: 'date', label: '날짜', render: (r) => boldIfTotal(r, r.date) },
  { key: 'revenue', label: '총 매출(실결제)', align: 'right', render: (r) => boldIfTotal(r, formatCurrency(r.revenue)) },
  { key: 'spend', label: '총 광고비', align: 'right', render: (r) => boldIfTotal(r, won(r.spend)) },
  { key: 'roas', label: 'ROAS(실결제)', align: 'right', render: (r) => boldIfTotal(r, r.roasText) },
  { key: 'metaRev', label: '인스타 전환매출(광고)', align: 'right', render: (r) => boldIfTotal(r, formatCurrency(r.metaRev)) },
  { key: 'spendBreakdown', label: '채널별 지출', align: 'right', render: (r) => boldIfTotal(r, r.spendBreakdown) },
]
